/* eslint-disable camelcase */
import {
    TRACKING_INFORMATION_ACTION_REVEAL,
    TRACKING_INFORMATION_UI_TYPE_FAQ,
    TRACKING_PLATFORM_TYPE_RESPONSIVE,
    TrackType,
    TrackingErrorType,
    TrackingLoginStatus,
    TrackingPageType,
    TrackingTargetAudience,
    trackIt,
} from '@vfde-sails/tracking';
import { AppState } from '../container/App/interface';
import { getSelectedRouter } from '../container/App/selector';
import { getHotlineNumbers, getTrackingOrder, pageLevels } from './util';
import { TrackingError, TrackingInformation, TrackingPayload } from './interface';
import {
    NAME_ACCORDION,
    NAME_FOOTNOTE,
    TRACKING_ERROR_ACTION,
    TRACKING_SITE_AREA,
} from './constants';
import { ApiErrorResponseWithCode } from '@vfde-sails/utils';
import { formatBitsToMbit, formatToTrackingString } from '../util/formatter';
import { TrackingBusinessTransactionType, TrackingCustomerFootprint, TrackingFlowType } from './enum';
import { debugInfo } from '../util/debug';

/**
 * Track pageView event
 * @param {AppState} state - current state of the application
 */
export const trackPageView = (state: AppState) => {
    try {
        const selected = getSelectedRouter(state);

        if (!selected) {
            return;
        }

        const { customer, providerType } = state;

        const isLoggedIn = Boolean(customer);

        const payload: TrackingPayload = {
            login_status: isLoggedIn ? TrackingLoginStatus.LoggedIn : TrackingLoginStatus.NotLoggedIn,
            page_levels: pageLevels,
            page_type: TrackingPageType.ProductDetail,
            site_area: TRACKING_SITE_AREA,
            platform_type: TRACKING_PLATFORM_TYPE_RESPONSIVE,
            oOrder: getTrackingOrder(selected, isLoggedIn),
            business_transaction_type: TrackingBusinessTransactionType[providerType],
            line_of_customer: TrackingTargetAudience.Consumer,
            flow_type: TrackingFlowType[providerType],
            hotline_numbers: getHotlineNumbers(),
        };

        if (customer) {
            payload.existing_customer_footprint = TrackingCustomerFootprint[providerType];
        }

        if (customer?.fixnetBandwidth) {
            payload.existing_customer_bandwidth = formatBitsToMbit(customer.fixnetBandwidth);
        }

        trackIt(TrackType.PageView, payload);
        debugInfo(TrackType.PageView, payload);
    } catch {
        /* Tracking Error, nothing to do */
    }
};

/**
 * Track error
 * @param {Error|ApiErrorResponseWithCode} error - error
 * @param {TrackingErrorType} type - tracking type of the error
 */
export const trackError = (error: Error | ApiErrorResponseWithCode, type: TrackingErrorType) => {
    try {
        const payload: TrackingError = {
            error_action: TRACKING_ERROR_ACTION,
            error_type: type,
            error_message: error.message,
            error_code: 'status' in error ? error.status.toString() : '',
        };

        trackIt(TrackType.Error, payload);
        debugInfo(TrackType.Error, payload);
    } catch {
        /* Tracking Error, nothing to do */
    }
};

/**
 * Track accordion opening
 * @param {string} content - the content of the accordion headline that triggered the opening
 */
export const trackFaqAccordionShown = (content: string) => {
    try {
        const payload: TrackingInformation = {
            information_action: TRACKING_INFORMATION_ACTION_REVEAL,
            information_name: formatToTrackingString(content),
            information_ui_type: TRACKING_INFORMATION_UI_TYPE_FAQ,
            information_trigger: NAME_ACCORDION,
        };

        trackIt(TrackType.Information, payload);
        debugInfo(TrackType.Information, payload);
    } catch {
        /* Tracking Error, nothing to do */
    }
};

/**
 * Track overlay opening
 * @param {string} name - name/description of element that triggered the opening
 */
export const trackOverlay = (name: string) => {
    try {
        const payload: TrackingInformation = {
            information_action: TRACKING_INFORMATION_ACTION_REVEAL,
            information_name: formatToTrackingString(name),
            information_ui_type: 'overlay',
            information_trigger: NAME_FOOTNOTE,
        };

        trackIt(TrackType.Information, payload);
        debugInfo(TrackType.Information, payload);
    } catch {
        /* Tracking Error, nothing to do */
    }
};
